<template>
    <div class="main-container px-1 pt-4  md:px-8 xl:px-16">
        <!-- Mobile View -->
        <div v-if="isMobile">
            <Accordion value="0" class="card-bg shadow-md rounded-xl">
                <AccordionPanel>
                    <AccordionHeader>
                        <span class="font-semibold text-lg pl-5">Date/Users Filters </span>
                    </AccordionHeader>
                    <AccordionContent>
                        <DateFilters v-model:dates="dates" v-model:selectedMonthIndex="selectedMonthIndex"
                            v-model:selectedDatePreset="selectedDatePreset" v-model:selectedUsers="selectedUsers"
                            :months="months" :datePresets="datePresets" :isMobile="isMobile" :showUserSelect="true"
                            :users="users" :loading="loading" @monthSelect="handleMonthSelect"
                            @datePresetChange="handleDatePresetChange" @userSelectedChange="handleUserSelectedChange" />
                    </AccordionContent>
                </AccordionPanel>
            </Accordion>

            <!-- Month Buttons for Mobile -->
            <div class="flex justify-center items-center gap-4 mt-3">
                <Button icon="pi pi-chevron-left" @click="navigateMonth(-1)" :disabled="!canNavigateBack"
                    severity="secondary" outlined class="p-button-text h-7" />
                <Button :severity="currentMonthLabel ? 'primary' : 'secondary'" class="min-w-[100px] font-semibold h-7">
                    {{ currentMonthLabel || 'Custom Range' }}
                </Button>
                <Button icon="pi pi-chevron-right" @click="navigateMonth(1)" :disabled="!canNavigateForward"
                    severity="secondary" outlined class="p-button-text h-7" />
            </div>
        </div>
        <!-- Desktop View -->
        <div v-else class="card-bg shadow-md rounded-xl p-4">
            <DateFilters v-model:dates="dates" v-model:selectedMonthIndex="selectedMonthIndex"
                v-model:selectedDatePreset="selectedDatePreset" v-model:selectedUsers="selectedUsers" :months="months"
                :datePresets="datePresets" :isMobile="isMobile" :showUserSelect="true" :users="users" :loading="loading"
                @monthSelect="handleMonthSelect" @datePresetChange="handleDatePresetChange"
                @userSelectedChange="handleUserSelectedChange" />
        </div>
        <Skeleton v-if="loading" class="mt-10" width="100%" height="60vh"></Skeleton>
        <DataTable v-else :key="selectedUsers.length" selectionMode="multiple" :virtualScrollerOptions="{ itemSize: 60 }"
            :value="filteredEarnings" dataKey="publisher.id" scrollable scrollDirection="both" :scrollHeight="'72svh'"
            :loading="loading" class="border border-gray-300 rounded-xl shadow-sm  
            md:col-span-1 mt-5"  sortMode="single" sortField="metrics.ourTotalRevenue"
            :sortOrder="-1" fluid :resizableColumns="true"
            v-model:expandedRows="expandedRows"
            v-model:selection="selectedRow"
            @selection-change="onSelectionChange">
            <!-- Expansion template -->
            <template v-if="!isSubAffiliate" #expansion="slotProps">
                <PublisherView
                    :publisher="slotProps.data"
                    :from="dates[0]"
                    :to="dates[1]"
                    @adjustmentSaved="refreshAfterAdjustment"
                />
            </template>

            <!-- Add expander column -->
            <Column :expander="true" v-if="!isSubAffiliate" :headerStyle="{
                ...headerStyles,
            }" :bodyStyle="{
                paddingLeft: '0',
                paddingRight: '0'
            }" />
            
            <Column header="Publisher" filterField="publisher.name" :headerStyle="{
                ...headerStyles,
            }" :bodyStyle="{
                paddingLeft: '10',
                paddingRight: '0'
            }">
                <template #body="slotProps">
                    <router-link v-if="!isSubAffiliate" :to="`/users/${slotProps.data.publisher.id}`" class="clickable-link">
                        {{ slotProps.data.publisher.name }}
                    </router-link>
                    <span v-else>{{ slotProps.data.publisher.name }}</span>
                </template>
            </Column>
            <Column  field="metrics.clicks" header="Clicks" sortable :footer="totalClicks" :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.uniqueClicks" header="UC's" sortable :footer="totalUniqueClicks"
                :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.signups" header="SU's" sortable :footer="totalSignups" :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.FTD" header="FTDs" sortable :footer="totalFTD" :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.deposits" header="D's" sortable :footer="totalDeposits" :headerStyle="headerStyles">
            </Column>
            <Column header="D value" sortable :footer="totalDepositValue"
                :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(slotProps.data.metrics.depositValue) }}</p> <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <Column field="metrics.CPA" header="CPAs" sortable :footer="totalCPAs" :headerStyle="headerStyles">
            </Column>
            <Column  header="Casino GGR" :headerStyle="headerStyles">
                <template #body="{ data }">
                    <p>{{ formatCurrency(data.metrics.casinoGGR) }}</p>
                </template>
            </Column>
            <!-- <Column header="Customer Deal" :headerStyle="headerStyles">
                <template #body="{ data }">
                    {{ getFriendlyDealMessage(data.customerDeal) }}
                </template>
            </Column> -->
            <Column  header="Pub rev" sortable :footer="totalCustomerRevenue"
                :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(getMetricsCustomerTotal(data.metrics)) }}
                        </p>
                        <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <!-- <Column header="Our Deal" :headerStyle="headerStyles">
                <template #body="{ data }">
                    {{ getFriendlyDealMessage(data.ourDeal) }}
                </template>
            </Column> -->
            <Column header="Our rev" sortable :footer="totalOurIncome" :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div :class="{ 'income-cell': getMetricsOurTotal(data.metrics) > 0 }">
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(getMetricsOurTotal(data.metrics)) }}</p>
                        <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup>
import { headerStyles } from '@/global/headerStyles';
import { FilterMatchMode } from '@primevue/core/api';
import { formatCurrency } from '@/utils/currencies';
import { ref, onMounted, reactive, watch, computed, onUnmounted } from "vue";
import { presets, getLastFourMonths, findCurrentMonthIndex } from '@/utils/dateHelper';
import EarningsService from "@/services/EarningsService";
import { getMetricsCustomerTotal, getMetricsOurTotal } from '@/utils/earnings';
import { getFriendlyDealMessage } from '@/utils/deals';
import DateFilters from '../Site/DateFilters.vue';
import PublisherView from './PublisherView.vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const isAdmin = computed(() => store.getters.isAdmin);
const isSubAffiliate = computed(() => store.getters.isSubAffiliate);
const router = useRouter();

// To render months - now using last 4 months instead of full year
const months = ref(getLastFourMonths());
const selectedMonthIndex = ref(findCurrentMonthIndex(months.value));

const updateUrlDates = (from, to) => {
    router.replace({
        query: {
            ...router.currentRoute.value.query,
            from: from.toISOString().split('T')[0],
            to: to.toISOString().split('T')[0]
        }
    });
};

const selectedMonth = (index, from, to) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [from, to];
    updateUrlDates(from, to);
};

const handleMonthSelect = ({ index, start, end }) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [start, end];
    updateUrlDates(start, end);
};

//grid data 
const filters = ref({
    userName: { matchMode: FilterMatchMode.IN }
});

//dates
const datePresets = ref(presets);
const selectedDatePreset = ref(null);
const dates = ref([
    months.value[selectedMonthIndex.value].start,
    months.value[selectedMonthIndex.value].end
]);

const handleDatePresetChange = () => {
    selectedMonthIndex.value = null; // Reset the selected month index
    console.log('Selected date preset changed to:', selectedDatePreset.value);
    dates.value = [selectedDatePreset.value.from, selectedDatePreset.value.to, 'preset'];
};

//users
const users = ref([]);
const selectedUsers = ref([]);
const populateUsers = (siteEarnings) => {
    const uniqueUsers = new Map();

    // Iterate through siteEarnings and populate the map with unique userId and userName pairs
    siteEarnings.forEach(site => {
        if (!uniqueUsers.has(site.publisher.id)) {
            uniqueUsers.set(site.publisher.id, site.publisher.name);
        }
    });

    // Convert the map back to an array of { userId, userName } objects
    users.value = Array.from(uniqueUsers, ([userId, userName]) => ({ userId, userName }));
};

const updateUrlWithSelectedUsers = (userIds) => {
    router.replace({
        query: {
            ...router.currentRoute.value.query,
            selectedUsers: userIds.length ? userIds.join(',') : undefined
        }
    });
};

const handleUserSelectedChange = () => {
    updateUrlWithSelectedUsers(selectedUsers.value);
};

//earnings 
const loading = ref(true);

const filteredEarnings = computed(() => {
    if (!selectedUsers.value.length) {
        // If no users are selected, show all site earnings
        return siteEarnings.value;
    }
    // Filter site earnings to show only the selected users
    let toRet = siteEarnings.value.filter(earning =>
        selectedUsers.value.some(selectedUserId => selectedUserId === earning.publisher.id)
    );
    return toRet;
});

const siteEarnings = ref([]);
const totalClicks = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.clicks || 0), 0));
const totalUniqueClicks = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.uniqueClicks || 0), 0));
const totalSignups = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.signups || 0), 0));
const totalFTD = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.FTD || 0), 0));
const totalCPAs = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.CPA || 0), 0));
const totalDeposits = computed(() => filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.deposits || 0), 0));
const totalDepositValue = computed(() => formatCurrency(filteredEarnings.value.reduce((sum, item) => sum + (item.metrics.depositValue || 0), 0)));
const totalCustomerRevenue = computed(() => formatCurrency(filteredEarnings.value.reduce((sum, item) => sum + getMetricsCustomerTotal(item.metrics), 0)));
const totalOurIncome = computed(() => formatCurrency(filteredEarnings.value.reduce((sum, item) => sum + getMetricsOurTotal(item.metrics), 0)));

async function GetSiteEarnings(from, to) {
    loading.value = true;
    resetEarnings();
    // await EarningsService.getByPublisher(from ?? selectedDatePreset.value.from, to ?? selectedDatePreset.value.to);
    await EarningsService.getByPublisher(from ?? selectedDatePreset.value.from, to ?? selectedDatePreset.value.to)
        .then((response) => {
            siteEarnings.value = response.data;

            // Populate users array first
            populateUsers(response.data);

            // If we have selectedUsers from URL, validate and filter them
            if (router.currentRoute.value.query.selectedUsers) {
                const userIds = router.currentRoute.value.query.selectedUsers.split(',');
                // Store only the IDs that exist in our users array
                const validUserIds = userIds.filter(id =>
                    users.value.some(user => user.userId === id)
                );
                selectedUsers.value = validUserIds;
            }
        })
        .finally(() => loading.value = false);
}

function resetEarnings() {
    siteEarnings.value = [];
}

/**
 * Refresh the publisher summary rows after an adjustment is saved in an
 * expanded breakdown.
 *
 * The expanded child table refetches itself, but the summary row above it comes
 * from this component's siteEarnings, so without this the row kept showing
 * pre-adjustment totals while the rows beneath it had already moved — the
 * parent read 0 CPAs / EUR0 next to a child showing 1 CPA / EUR430.
 *
 * Deliberately NOT calling GetSiteEarnings: that runs resetEarnings() first,
 * which empties the table and drops the loading spinner over the expansion the
 * operator is still reading. This refetches in place and swaps the data in one
 * assignment. expandedRows is keyed by publisher.id rather than row identity,
 * so the open row survives the swap.
 */
async function refreshAfterAdjustment() {
    try {
        const response = await EarningsService.getByPublisher(dates.value[0], dates.value[1]);
        siteEarnings.value = response.data;
    } catch (error) {
        console.error('Error refreshing publisher earnings after adjustment:', error);
    }
}


watch(dates, async (newDate) => {
    if (newDate[1] !== null) {
        if (newDate[2] !== 'preset')
            selectedDatePreset.value = null;

        if (newDate[1].getHours() === 0)
            selectedMonthIndex.value = null; // Reset the selected month index as its coming from the date picker
        console.log('triggerin request');
        await GetSiteEarnings(newDate[0], newDate[1]);
    }
});

watch(siteEarnings, (newSiteEarnings) => {
    populateUsers(newSiteEarnings);
});

onMounted(async () => {
    const { from, to } = router.currentRoute.value.query;

    // Handle dates
    if (from && to) {
        dates.value = [new Date(from), new Date(to)];

        // Check if dates match any month
        const monthIndex = months.value.findIndex(month => {
            const monthStart = month.start.toISOString().split('T')[0];
            const monthEnd = month.end.toISOString().split('T')[0];
            return from === monthStart && to === monthEnd;
        });

        if (monthIndex !== -1) {
            selectedMonthIndex.value = monthIndex;
        } else {
            selectedMonthIndex.value = null;
        }
    }
    else {
        // Load earnings first to populate users array
        await GetSiteEarnings(
            months.value[selectedMonthIndex.value].start,
            months.value[selectedMonthIndex.value].end
        );
    }


});

// Add mobile detection
const isMobile = ref(window.innerWidth <= 768);

const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
};

const canNavigateBack = computed(() => selectedMonthIndex.value > 0);
const canNavigateForward = computed(() => selectedMonthIndex.value < months.value.length - 1);
const currentMonthLabel = computed(() => months.value[selectedMonthIndex.value]?.label || '');

const navigateMonth = (direction) => {
    const newIndex = selectedMonthIndex.value + direction;
    
    if (newIndex >= 0 && newIndex < months.value.length) {
        selectedMonth(newIndex, months.value[newIndex].start, months.value[newIndex].end);
    }
};


onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const expandedRows = ref({});
const selectedRow = ref(null);

const onSelectionChange = (event) => {
    if (isSubAffiliate.value) {
        return;
    }
    if (event.value && Array.isArray(event.value)) {
        // Get the last selected row (most recent selection)
        const lastSelected = event.value[event.value.length - 1];
        
        // Toggle the expansion state for the last selected row
        expandedRows.value = {
            ...expandedRows.value,
            [lastSelected.publisher.id]: !expandedRows.value[lastSelected.publisher.id]
        };
    }
};

watch(selectedRow, (newValue) => {
    if (isSubAffiliate.value) {
        return;
    }
    if (newValue && Array.isArray(newValue) && newValue.length > 0) {
        // Get the last selected row
        const lastSelected = newValue[newValue.length - 1];
        
        // Toggle the expansion state for the last selected row
        expandedRows.value = {
            ...expandedRows.value,
            [lastSelected.publisher.id]: !expandedRows.value[lastSelected.publisher.id]
        };
    }
});
</script>

<style scoped>
.p-accordionheader {
    padding: 5px !important;
}

.p-accordionpanel {
    border-width: 0 0 0 0;
}
</style>